import { useState, useEffect, useCallback } from 'react';
import { FiUserPlus, FiTrash2, FiMail, FiRefreshCw, FiX, FiChevronLeft, FiChevronRight, FiClock } from 'react-icons/fi';
import API_URL from '../config';

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtDay(dt) {
  return new Date(dt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}
function hoursLabel(h) {
  if (h == null) return '—';
  const hrs = Math.floor(h);
  const min = Math.round((h - hrs) * 60);
  return min > 0 ? `${hrs}h ${min}m` : `${hrs}h`;
}
function monthBounds(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end   = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { start, end, label: start.toLocaleDateString([], { month: 'long', year: 'numeric' }) };
}

const ROLE_COLORS = {
  OWNER:   'bg-purple-100 text-purple-700',
  MANAGER: 'bg-blue-100 text-blue-700',
  EMPLOYEE:'bg-slate-100 text-slate-600',
};

export default function Employees({ token, user }) {
  const [members, setMembers]   = useState([]);
  const [invites, setInvites]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole]   = useState('EMPLOYEE');
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  // Attendance drawer state
  const [drawer, setDrawer] = useState(null); // { id: numeric, name: string }
  const [monthOffset, setMonthOffset] = useState(0);
  const [attRecords, setAttRecords]   = useState([]);
  const [attLoading, setAttLoading]   = useState(false);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/team`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) { setMembers(d.members || []); setInvites(d.pending_invites || []); }
    } catch {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  // Fetch attendance for drawer employee whenever drawer or month changes
  useEffect(() => {
    if (!drawer) return;
    const { start, end } = monthBounds(monthOffset);
    const pad = n => String(n).padStart(2, '0');
    const fmt2 = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    setAttLoading(true);
    fetch(`${API_URL}/api/employee/employees/${drawer.id}/attendance?start_date=${fmt2(start)}&end_date=${fmt2(end)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setAttRecords(d); else setAttRecords([]); })
      .catch(() => setAttRecords([]))
      .finally(() => setAttLoading(false));
  }, [drawer, monthOffset, token]);

  function openDrawer(member) {
    // Only kiosk employees have attendance (id is 'emp-123')
    if (member.type !== 'employee') return;
    const numericId = String(member.id).replace('emp-', '');
    setDrawer({ id: numericId, name: member.name });
    setMonthOffset(0);
    setAttRecords([]);
  }

  const sendInvite = async (e) => {
    e.preventDefault();
    setSending(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_URL}/api/auth/team/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      const d = await res.json();
      if (res.ok) {
        setSuccess(d.message || 'Invite sent!');
        setInviteEmail(''); setShowInvite(false);
        fetchTeam();
      } else { setError(d.error || 'Failed to send invite.'); }
    } catch { setError('Connection error.'); } finally { setSending(false); }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this member?')) return;
    await fetch(`${API_URL}/api/auth/team/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchTeam();
  };

  const cancelInvite = async (id) => {
    if (!window.confirm('Cancel this invite?')) return;
    await fetch(`${API_URL}/api/auth/team/invites/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchTeam();
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Employees</h2>
        <div className="flex gap-2">
          <button onClick={fetchTeam} className="p-2 text-slate-500 hover:text-slate-700 transition-colors">
            <FiRefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setShowInvite(v => !v); setError(''); setSuccess(''); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <FiUserPlus size={14} /> Invite
          </button>
        </div>
      </div>

      {success && <p className="mb-3 text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{success}</p>}
      {error   && <p className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

      {showInvite && (
        <form onSubmit={sendInvite} className="mb-4 bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            required
            placeholder="Email address"
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
          />
          <select
            value={inviteRole}
            onChange={e => setInviteRole(e.target.value)}
            className="w-full sm:w-36 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
          >
            <option value="EMPLOYEE">Employee</option>
            <option value="MANAGER">Manager</option>
          </select>
          <button
            type="submit"
            disabled={sending}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm min-w-[400px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Name</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Email</th>
              <th className="text-left px-4 py-3 text-slate-500 font-medium">Role</th>
              {user.role === 'OWNER' && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {members.map(m => (
              <tr key={m.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">
                  {m.type === 'employee' ? (
                    <button
                      onClick={() => openDrawer(m)}
                      className="text-blue-600 hover:underline font-medium text-left flex items-center gap-1.5"
                    >
                      {m.name}
                      <FiClock size={12} className="text-slate-400" />
                    </button>
                  ) : m.name}
                </td>
                <td className="px-4 py-3 text-slate-500">{m.email}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[m.role] || ''}`}>{m.role}</span>
                </td>
                {user.role === 'OWNER' && (
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => removeMember(m.id)} className="text-slate-400 hover:text-red-500 transition-colors">
                      <FiTrash2 size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No team members yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {invites.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium text-slate-600 mb-2">Pending Invites</h3>
          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {invites.map(inv => (
              <div key={inv.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2 text-sm">
                  <FiMail size={14} className="text-slate-400" />
                  <span className="text-slate-700">{inv.email}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[inv.role] || ''}`}>{inv.role}</span>
                </div>
                <button onClick={() => cancelInvite(inv.id)} className="text-slate-400 hover:text-red-500 transition-colors" title="Cancel invite">
                  <FiX size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attendance Drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div className="flex-1 bg-black/30" onClick={() => setDrawer(null)} />
          {/* Panel */}
          <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-800">{drawer.name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">Attendance History</p>
              </div>
              <button onClick={() => setDrawer(null)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <FiX size={18} />
              </button>
            </div>

            {/* Month navigation */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <button onClick={() => setMonthOffset(v => v - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <FiChevronLeft size={18} />
              </button>
              <span className="text-sm font-medium text-slate-700">{monthBounds(monthOffset).label}</span>
              <button onClick={() => setMonthOffset(v => v + 1)} disabled={monthOffset >= 0} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-30">
                <FiChevronRight size={18} />
              </button>
            </div>

            {/* Summary bar */}
            {!attLoading && attRecords.length > 0 && (
              <div className="px-5 py-2.5 bg-blue-50 border-b border-blue-100 flex items-center gap-2 text-sm text-blue-700">
                <FiClock size={14} className="text-blue-500" />
                <span>
                  {attRecords.length} day{attRecords.length !== 1 ? 's' : ''} worked ·{' '}
                  <strong>{hoursLabel(attRecords.reduce((s, r) => s + (r.duration || 0), 0))}</strong> total
                </span>
              </div>
            )}

            {/* Records */}
            <div className="flex-1 overflow-y-auto">
              {attLoading && (
                <p className="px-5 py-10 text-center text-slate-400 text-sm">Loading…</p>
              )}
              {!attLoading && attRecords.length === 0 && (
                <p className="px-5 py-10 text-center text-slate-400 text-sm">No records for this month.</p>
              )}
              {!attLoading && attRecords.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      <th className="text-left px-5 py-3 text-slate-500 font-medium">Date</th>
                      <th className="text-left px-5 py-3 text-slate-500 font-medium">In</th>
                      <th className="text-left px-5 py-3 text-slate-500 font-medium">Out</th>
                      <th className="text-left px-5 py-3 text-slate-500 font-medium">Hours</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {attRecords.map(r => (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-5 py-3 text-slate-600">{fmtDay(r.clock_in)}</td>
                        <td className="px-5 py-3 text-slate-700">{fmt(r.clock_in)}</td>
                        <td className="px-5 py-3 text-slate-700">
                          {r.clock_out ? fmt(r.clock_out) : <span className="text-amber-500 text-xs font-medium">Missing</span>}
                        </td>
                        <td className="px-5 py-3 font-medium text-slate-800">{hoursLabel(r.duration)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
